import { Headphones, Loader2, Lock, Pause, RefreshCw } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import type { SectionPodcastSummary } from '@/lib/api-client';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  useGenerateSectionPodcast,
  useListenToSection,
  useSectionPodcastSummary,
} from '@/hooks/lesson/useSectionPodcasts';
import { podcastPhase } from './podcastPhase';

interface ButtonState {
  label: string;
  icon: typeof Headphones;
  spin?: boolean;
  disabled?: boolean;
  /** `listen` plays (or pauses) it; `generate` makes it. */
  action: 'listen' | 'generate' | 'none';
}

const LOADING: ButtonState = {
  label: 'Loading',
  icon: Loader2,
  spin: true,
  disabled: true,
  action: 'none',
};

function stateFor(
  summary: SectionPodcastSummary,
  current: { playing: boolean } | null,
  busy: boolean
): ButtonState {
  switch (podcastPhase(summary, busy)) {
    case 'ready':
      if (busy) return LOADING;
      if (current?.playing) {
        return { label: 'Pause', icon: Pause, action: 'listen' };
      }
      return {
        label: current ? 'Resume' : 'Listen',
        icon: Headphones,
        action: 'listen',
      };
    case 'making':
      return { ...LOADING, label: 'Making podcast' };
    case 'failed':
      return { label: 'Retry podcast', icon: RefreshCw, action: 'generate' };
    case 'locked':
      // Generating answers with the Plus explanation (402 upgrade_required).
      return { label: 'Podcast · Plus', icon: Lock, action: 'generate' };
    default:
      return { label: 'Make podcast', icon: Headphones, action: 'generate' };
  }
}

interface SectionListenButtonProps {
  lessonId: string | undefined;
  sectionIndex: number;
  className?: string;
}

/**
 * A section's podcast as one button, for the Podcast tab's list rows: make
 * it, then listen to it in the app's player (the Text reader has the fuller
 * `SectionAudioStrip`). One
 * unit of the podcast quota per section; on Free, the first section only.
 * Real data only: mock and empty modes have no podcasts to make.
 */
export function SectionListenButton(props: SectionListenButtonProps) {
  const { isApi } = useDataMode();
  return isApi ? <ListenButton {...props} /> : null;
}

function ListenButton({
  lessonId,
  sectionIndex,
  className,
}: SectionListenButtonProps) {
  const summary = useSectionPodcastSummary(lessonId, sectionIndex);
  const generate = useGenerateSectionPodcast(lessonId);
  const { listen, loading, player } = useListenToSection(lessonId);
  if (!summary || !lessonId || !player) return null;

  const current = player.isCurrent(lessonId, sectionIndex)
    ? { playing: player.isPlaying }
    : null;
  const state = stateFor(
    summary,
    current,
    loading === sectionIndex ||
      (generate.isPending && generate.variables === sectionIndex)
  );
  const Icon = state.icon;
  const onClick = () => {
    if (state.action === 'listen') void listen(sectionIndex);
    else if (state.action === 'generate') generate.mutate(sectionIndex);
  };

  return (
    <Button
      variant='outline'
      size='sm'
      onClick={onClick}
      disabled={state.disabled}
      aria-label={`${state.label}: podcast of section ${sectionIndex}`}
      className={cn('tap-target h-8 gap-1.5 px-2.5', className)}
    >
      <Icon
        className={cn('h-3.5 w-3.5', state.spin && 'animate-spin')}
        aria-hidden='true'
      />
      {state.label}
    </Button>
  );
}
